import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiError, apiSend } from '../../lib/api';
import { Alert, Card, LoadingPage, PageHeader } from '../../ui';

/**
 * The page the confirmation link lands on.
 *
 * The token is redeemed by a POST, not by the GET that loaded the page: the
 * grant is single-use, and a link that consumes itself on a GET is consumed by
 * the recipient's mail scanner before the recipient ever clicks it.
 */
export function VerifyEmail(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'failed'>(
    token ? 'working' : 'failed',
  );
  const [message, setMessage] = useState<string>(
    token ? '' : 'That link is missing its confirmation code.',
  );
  // React 19 StrictMode mounts effects twice in development; a single-use token
  // redeemed twice would report failure on the second pass.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    void (async () => {
      try {
        await apiSend('POST', '/auth/verify-email', { token });
        setState('done');
      } catch (err) {
        setState('failed');
        setMessage(
          err instanceof ApiError ? err.message : 'Could not reach the API to confirm the address.',
        );
      }
    })();
  }, [token]);

  if (state === 'working') {
    return <LoadingPage eyebrow="Nexus" title="Confirming your email" />;
  }

  if (state === 'done') {
    return (
      <>
        <PageHeader
          eyebrow="Nexus"
          title="Email confirmed"
          lead="That address is yours, and your account is live."
        />
        <Card variant="feature">
          <Alert tone="positive">Your email address is confirmed.</Alert>
          <p className="muted">
            Sign in, connect an account you post from, and join a campaign. That is the whole
            setup.
          </p>
          <div className="actions">
            <Link className="btn btn-primary" to="/login">
              Sign in
            </Link>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="Nexus" title="That link did not work" />
      <Card>
        <Alert tone="critical">{message}</Alert>
        <p className="muted">
          Confirmation links last 24 hours and work once — a second click on the same link is
          refused, even though the first one worked.
        </p>
        <div className="actions">
          <Link className="btn btn-ghost" to="/login">
            Sign in and ask for a new one
          </Link>
        </div>
      </Card>
    </>
  );
}
